// // Mute TS About roptoSession in Window
declare const window: Window & { roptoSession: any };

interface ServiceOptsModel {
  productId: string;
  clientId: string;
}

interface ApiServiceInitConfig {
  baseUrl: string;
  opts: ServiceOptsModel;
  initHeaders?: HeadersInit;
  serviceName?: string;
}

class ApiService {
  private readonly serviceName: string;

  readonly environment: string;

  logger = (...args) => {
    if (this.environment === 'dev') {
      // eslint-disable-next-line no-console
      console.log(`%c[${this.serviceName}] :: `, 'color:DodgerBlue', args.join(''));
    }
  };

  private defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };

  productId: string;

  clientId: string;

  private readonly baseOptions: RequestInit;

  private readonly baseUrl: string;

  constructor(args: ApiServiceInitConfig) {
    const { baseUrl, opts, initHeaders, serviceName } = args;
    this.serviceName = serviceName || this.constructor.name;
    this.productId = opts.productId;
    this.clientId = opts.clientId;
    this.baseUrl = baseUrl;
    this.baseOptions = { headers: { ...this.defaultHeaders, ...initHeaders } };
    this.environment = process.env.STAGE !== 'production' ? 'dev' : 'prod';
    this.logger('Environment: ', this.environment);
  }

  /** ----------------- Getter/Setter Methods -------------------------  */

  set Headers(headers: HeadersInit) {
    this.baseOptions.headers = headers;
  }

  get Headers(): HeadersInit {
    return this.baseOptions.headers;
  }

  private async fetchRequest<T>(url: string, opts: RequestInit): Promise<T> {
    return (await fetch(url, opts)).json();
  }

  /** ----------------- Public Methods -------------------------  */
  public get sessionValues() {
    const sessionData = window.roptoSession.getSession();
    
    return {
      ...sessionData,
      productId: this.productId,
      clientId: this.clientId,
      acquisition: {
        brandId: 21,
        source: sessionData.channel,
        adId: sessionData.adId,
      },
    };
  }

  addHeaders(headers: HeadersInit) {
    this.Headers = { ...this.Headers, ...headers };
  }

  async get<T>(path: string, opts: RequestInit = this.baseOptions): Promise<T> {
    return this.fetchRequest<T>(`${this.baseUrl}/${path}`, { ...opts, method: 'GET' });
  }

  async post<T>(path: string, body: any, opts: RequestInit = this.baseOptions): Promise<T | undefined> {
    return this.fetchRequest<T>(`${this.baseUrl}/${path}`, {
      body: JSON.stringify(body),
      ...opts,
      method: 'POST',
    });
  }

  async put<T>(path: string, body: any, opts: RequestInit = this.baseOptions): Promise<T | undefined> {
    return this.fetchRequest<T>(`${this.baseUrl}/${path}`, {
      body: JSON.stringify(body),
      ...opts,
      method: 'PUT',
    });
  }

  async patch<T>(path: string, body: any, opts: RequestInit = this.baseOptions): Promise<T | undefined> {
    return this.fetchRequest<T>(`${this.baseUrl}/${path}`, {
      body: JSON.stringify(body),
      ...opts,
      method: 'PATCH',
    });
  }
}

export { ApiService };
